<template>
<div>
    <!-- HERO SECTION -->
    <section class="hero">
        <div class="hero-background fade-in">
            <img v-for="(img, index) in heroImages" :key="index" :src="img" :class="{ active: index === heroIndex }" alt="Agriculture" loading="lazy" />
        </div>

        <div class="hero-left slide-in-left">
            <div class="hero-content">
                <div class="hero-kicker fade-in-up">
                    <span class="kicker-label">MNK AgriVenture</span>
                    <span class="kicker-subline">{{$t('Tagline')}}</span>
                </div>

                <h1 class="hero-title fade-in-up delay-1">
                    {{$t('FromOurFarms')}}<br />
                    <span>{{$t('ToTheWorld')}}</span>
                </h1>

                <p class="hero-subtitle fade-in-up delay-2">
                    {{ $t('TaglineDescription') }}
                </p>

                <div class="hero-buttons fade-in-up delay-3">
                    <button class="btn-primary bounce-hover" @click="goToServices">
                        {{$t('ExploreVentures')}} <span class="arrow">→</span>
                    </button>

                    <button class="btn-secondary bounce-hover" @click="learnMore">
                        {{$t('Learn More')}}
                        <v-icon right>mdi-information-outline</v-icon>
                    </button>
                </div>

            </div>
        </div>
    </section>

    <!-- WHO WE ARE SECTION -->
    <section class="content-section fade-in-up">
        <v-container fluid class="my-10">
            <v-row align="center" justify="space-between" dense>
                <v-col cols="12" md="6" class="summary-right slide-in-right">
                    <div class="image-wrapper zoom-in fade-in">
                        <transition name="fade" mode="out-in">
                            <img :src="aboutImage" :key="aboutIndex" alt="Who We Are" class="summary-image" loading="lazy" />
                        </transition>
                    </div>

                </v-col>

                <v-col cols="12" md="6" class="summary-left slide-in-left">
                    <div class="summary-content">
                        <div class="lux-header">

                            <h2 class="section-title">
                                {{ $t('Who We Are') }}
                            </h2>

                        </div>

                        <p>{{$t('AboutUsText')}}</p>
                        <v-btn color="green darken-2" class="mt-6 btn-design pulse-hover" rounded="xl" flat to="/about-us">
                            {{$t( 'Learn More')}}
                            <v-icon right>mdi-arrow-right</v-icon>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </section>

    <!-- WHAT WE DO SECTION -->
    <section class="content-section fade-in-up">
        <v-container>
            <div class="lux-header text-center">

                <h2 class="section-title">
                    {{ $t('What We Do') }}
                </h2>

            </div>

            <!-- Central Image -->
            <div class="central-image-wrapper fade-in">
                <img :src="serviceImage" alt="Agriculture Service" class="central-image" loading="lazy" />
            </div>

            <!-- Services Grid -->
            <v-row justify="center" class="gap-6 mt-12">
                <v-col v-for="(item, index) in whatWeDoPoints" :key="index" cols="12" sm="6" md="4" lg="3">
                    <div class="service-card" @click="toggleExpand(index)">
                        <div class="icon-wrapper">
                            <v-icon size="50" color="green darken-2">{{ item.icon }}</v-icon>
                        </div>

                        <h3 class="service-title">{{ $t(item.titleKey) }}</h3>
                        <p class="service-description">{{ $t(item.descriptionKey) }}</p>

                        <v-expand-transition>
                            <div v-if="expandedIndex === index" class="service-more">
                                <p>{{ $t(item.moreKey) }}</p>
                            </div>
                        </v-expand-transition>

                        <p class="learn-more-link">
                            {{ expandedIndex === index ? $t("ShowLess") : $t("LearnMore") }}
                            <v-icon size="18" class="ml-1">
                                {{ expandedIndex === index ? "mdi-chevron-up" : "mdi-chevron-down" }}
                            </v-icon>
                        </p>
                    </div>
                </v-col>

            </v-row>

            <!-- View All Button -->
            <div class="text-center mt-12">
                <v-btn color="green darken-2" class="btn-design pulse-hover" rounded="xl" flat to="/services">
                    {{$t('ViewAllServices')}}
                    <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
            </div>
        </v-container>
    </section>

    <!-- WHY CHOOSE US SECTION -->
    <section class="content-section">
        <v-container class="py-6">
            <div class="lux-header text-center">

                <h2 class="section-title">
                    {{ $t('WhyChooseMNKAgriventures') }}
                </h2>

            </div>

            <div class="timeline">
                <div v-for="(point, index) in whyChoose" :key="index" class="timeline-item">
                    <!-- Node -->
                    <div class="timeline-node">
                        <div class="timeline-icon">
                            <v-icon size="28" color="white">{{ point.icon }}</v-icon>
                        </div>
                    </div>

                    <!-- Card -->
                    <div :class="['timeline-card', index % 2 === 0 ? 'left' : 'right']">
                        <h3>{{$t(point.titleKey) }}</h3>
                        <p>{{ $t(point.descriptionKey) }}</p>
                    </div>
                </div>

                <!-- Central vertical line -->
                <div class="timeline-line"></div>
            </div>
        </v-container>
    </section>

    <!-- ===== EXPORT EXPERIENCE – LUXURY & DOMESTIC ===== -->
    <section class="export-lux-section">
        <v-container>

            <!-- HEADER -->
            <div class="lux-header text-center mb-12">
                <!-- <span class="lux-eyebrow">Global & Local Reach</span> -->
                <h2 class="section-title">
                    {{ $t('MarketExperience') }}
                </h2>
            </div>
            <div class="lux-subtitle">
                <p class="">
                    {{ $t("MarketsExperienceText") }}<br />

                    {{ $t("ProjectsExperienceText") }}
                </p>
            </div>

            <!-- STATS -->
            <v-row justify="center" class="lux-stats">
                <v-col v-for="(stat, i) in exportStats" :key="i" cols="12" sm="6" md="3" class="lux-stat-col">
                    <div class="lux-stat-card">
                        <!-- Centered Icon -->
                        <div class="lux-icon">
                            <v-icon size="30">{{ stat.icon }}</v-icon>
                        </div>

                        <!-- Stat Value -->
                        <h3 class="lux-stat-value">{{ $t(stat.valueKey) }}</h3>

                        <!-- Label -->
                        <span class="lux-stat-label">{{ $t(stat.labelKey) }}</span>
                    </div>
                </v-col>
            </v-row>

            <!-- CROPS -->
            <h3 class="lux-section-title text-center mb-6">
                {{ $t('CoreCommodities') }}
            </h3>

            <v-row class="lux-crops">
                <v-col v-for="(crop, i) in exportCrops" :key="i" cols="12" sm="6" md="3">
                    <div class="lux-crop-card">
                        <img :src="crop.image" />
                        <div class="lux-crop-overlay">
                            <h4>{{ $t(crop.nameKey) }}</h4>
                            <p>{{ $t(crop.descriptionKey) }}</p>
                        </div>
                    </div>
                </v-col>
            </v-row>

            <!-- COUNTRIES + DOMESTIC MARKET -->
            <h3 class="lux-section-title text-center mb-6">
                {{ $t('MarketsServed') }}
            </h3>

            <div class="lux-countries-wrapper">
                <div class="lux-countries-track">
                    <!-- International -->
                    <div v-for="(country, i) in exportCountries" :key="'intl-'+i" class="lux-country-card floating">
                        <img :src="country.flag" alt="" />
                        <span>{{ country.name }}</span>
                    </div>

                    <!-- Domestic -->
                    <div v-for="(dom, i) in domesticMarkets" :key="'dom-'+i" class="lux-country-card floating domestic">
                        <v-icon size="32" color="#2f6b4f">mdi-map-marker</v-icon>
                        <span>{{ dom.name }}</span>
                    </div>

                    <!-- duplicate for seamless motion -->
                    <div v-for="(country, i) in exportCountries" :key="'dup-intl-'+i" class="lux-country-card floating">
                        <img :src="country.flag" alt="" />
                        <span>{{ country.name }}</span>
                    </div>
                    <div v-for="(dom, i) in domesticMarkets" :key="'dup-dom-'+i" class="lux-country-card floating domestic">
                        <v-icon size="32" color="#2f6b4f">mdi-map-marker</v-icon>
                        <span>{{ dom.name }}</span>
                    </div>
                </div>
            </div>

        </v-container>
    </section>

    <!-- TESTIMONIALS SECTION -->
    <section class="testimonials-section fade-in-up" v-if="testimonials && testimonials.length">
        <v-container>
            <div class="lux-header text-center mb-12">

                <h2 class="section-title">
                    {{ $t('WhatPeopleSayAboutUs') }}
                </h2>

            </div>

            <div class="testimonial-slider">
                <div class="testimonial-track" :style="{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }">
                    <div v-for="(testimonial, index) in testimonials" :key="index" class="testimonial-slide">
                        <div class="testimonial-card">
                            <p class="testimonial-text">“{{ testimonial.message }}”</p>
                            <div class="testimonial-name">— {{ testimonial.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </v-container>
    </section>

</div>
</template>

<script>
import serviceImage from '@/assets/S11.jpeg';

import axios from 'axios';
export default {
    data() {
        return {
            // heroImage: s2Image,

            serviceImage: serviceImage,
            expandedIndex: null,

            heroImage: require('@/assets/karafuu.jpg'),
            heroIndex: 0,
            currentIndex: 0,
            itemsPerView: 3, // show 3 testimonials per view
            intervalId: null,
            aboutImages: [
                require('@/assets/tangawizi2.jpg'),
                require('@/assets/pilipili g.jpg'),
                require('@/assets/korosho2.jpg'),

            ],
            heroImages: [
                require('@/assets/mdalasini1.jpg'),
                require('@/assets/karafuu.jpg'),
                require('@/assets/kahawa.jpg'),

            ],
            aboutIndex: 0,
            aboutImage: require('@/assets/korosho2.jpg'), // initia
            whatWeDoPoints: [{
                    titleKey: "WhatWeDo.CultivationProduction.title",
                    descriptionKey: "WhatWeDo.CultivationProduction.description",
                    moreKey: "WhatWeDo.CultivationProduction.more",
                    icon: "mdi-sprout",
                },
                {
                    titleKey: "WhatWeDo.Procurement.title",
                    descriptionKey: "WhatWeDo.Procurement.description",
                    moreKey: "WhatWeDo.Procurement.more",
                    icon: "mdi-basket",
                },
                {
                    titleKey: "WhatWeDo.FoodProcessing.title",
                    descriptionKey: "WhatWeDo.FoodProcessing.description",
                    moreKey: "WhatWeDo.FoodProcessing.more",
                    icon: "mdi-food-variant",
                },
                {
                    titleKey: "WhatWeDo.FarmerTraining.title",
                    descriptionKey: "WhatWeDo.FarmerTraining.description",
                    moreKey: "WhatWeDo.FarmerTraining.more",
                    icon: "mdi-school",
                },
            ],

            exportStats: [{
                    icon: "mdi-calendar-star",
                    valueKey: "exportStats.ExportExperience.value",
                    labelKey: "exportStats.ExportExperience.label"
                },
                {
                    icon: "mdi-earth",
                    valueKey: "exportStats.MarketsServed.value",
                    labelKey: "exportStats.MarketsServed.label"
                },
                {
                    icon: "mdi-handshake",
                    valueKey: "exportStats.TrustedPartners.value",
                    labelKey: "exportStats.TrustedPartners.label"
                }
            ],
            exportCrops: [{
                    nameKey: "exportCrops.KRO.name",
                    image: require('@/assets/korosho2.jpg'),
                    descriptionKey: "exportCrops.KRO.description"
                },
                {
                    nameKey: "exportCrops.KRF.name",
                    image: require('@/assets/karafuu.jpg'),
                    descriptionKey: "exportCrops.KRF.description"
                },
                {
                    nameKey: "exportCrops.KHW.name",
                    image: require('@/assets/kahawa.jpg'),
                    descriptionKey: "exportCrops.KHW.description"
                },
                {
                    nameKey: "exportCrops.TNW.name",
                    image: require('@/assets/tangawizi2.jpg'),
                    descriptionKey: "exportCrops.TNW.description"
                }
            ],

            exportCountries: [{
                    name: "India",
                    flag: "https://flagcdn.com/w80/in.png"
                },
                {
                    name: "Kenya",
                    flag: "https://flagcdn.com/w80/ke.png"
                },
                {
                    name: "Comoros",
                    flag: "https://flagcdn.com/w80/km.png"
                },
                {
                    name: "Somalia",
                    flag: "https://flagcdn.com/w80/so.png"
                }
            ],
            // domesticMarkets: [{
            //         name: 'Dar es Salaam'
            //     },
            //     {
            //         name: 'Arusha'
            //     },
            //     {
            //         name: 'Mwanza'
            //     },
            //     {
            //         name: 'Dodoma'
            //     },
            // ],

            whyChoose: [{
                    titleKey: "WhyChoose.EndToEnd.title",
                    descriptionKey: "WhyChoose.EndToEnd.description",
                    icon: "mdi-truck"
                },
                {
                    titleKey: "WhyChoose.ExpertLed.title",
                    descriptionKey: "WhyChoose.ExpertLed.description",
                    icon: "mdi-account-tie"
                },
                {
                    titleKey: "WhyChoose.Sustainable.title",
                    descriptionKey: "WhyChoose.Sustainable.description",
                    icon: "mdi-leaf"
                },
                {
                    titleKey: "WhyChoose.Technology.title",
                    descriptionKey: "WhyChoose.Technology.description",
                    icon: "mdi-desktop-mac"
                },
                {
                    titleKey: "WhyChoose.Global.title",
                    descriptionKey: "WhyChoose.Global.description",
                    icon: "mdi-earth"
                }
            ],
            testimonials: [],
            scrollSpeed: 0.5
        };
    },
    computed: {
        // duplicatedCards() {
        //     return [...this.whyChoose, ...this.whyChoose];
        // }
    },

    methods: {

        openDialog(member) {
            this.selectedMember = member;
            this.dialog = true;
        },
        rotateHeroImage() {
            this.heroIndex = (this.heroIndex + 1) % this.heroImages.length;
            this.heroImage = this.heroImages[this.heroIndex];
        },
        rotateAboutImage() {
            this.aboutIndex = (this.aboutIndex + 1) % this.aboutImages.length;
            this.aboutImage = this.aboutImages[this.aboutIndex];
        },
        setInitialCardHeights() {
            const cards = this.$el.querySelectorAll('.service-card');
            let maxHeight = 0;

            cards.forEach(card => {
                const cardHeight = card.offsetHeight;
                if (cardHeight > maxHeight) maxHeight = cardHeight;
            });

            cards.forEach(card => {
                card.style.minHeight = maxHeight + 'px'; // equal height
            });
        },
        toggleExpand(index) {
            this.expandedIndex = this.expandedIndex === index ? null : index;
        },
        goToServices() {
            // Navigate programmatically
            this.$router.push('/services');
        },
        async fetchTestimonials() {
            try {
                const response = await axios.get("/publish-comment");
                this.testimonials = response.data.data || [];
                console.log(this.testimonials)
            } catch (error) {
                console.error("Error fetching testimonials:", error);
                this.testimonials = [];
            }
        },
        async loadAddress() {
            try {
                const response = await axios.get("/company-address", {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                });
                if (response.data && response.data.data) {
                    this.form = {
                        ...response.data.data,
                    };
                }
            } catch (error) {
                console.error("Failed to load company address:", error);
            }
        },

        learnMore() {
            // You can navigate or do anything else
            this.$router.push('/about-us');
            // or perform other logic
            console.log('Learn More clicked');
        },
        startRotation() {
            this.intervalId = setInterval(() => {
                if (this.currentIndex < this.testimonials.length - this.itemsPerView) {
                    this.currentIndex++;
                } else {
                    this.currentIndex = 0;
                }
            }, 5000); // rotate every 3.5 seconds
        },

    },
    mounted() {
        this.$nextTick(() => {
            this.setInitialCardHeights();
        });
        setInterval(() => {
            this.heroIndex = (this.heroIndex + 1) % this.heroImages.length;
        }, 5000);
        // About section rotation
        setInterval(() => {
            this.rotateAboutImage();
        }, 10000);
        this.startRotation();
        this.fetchTestimonials();
    },
    beforeUnmount() {
        clearInterval(this.intervalId);
    },
};
</script>

<style scoped>
/* 🔹 Common Section Background */
.content-section {
    padding: 3rem 1rem;
    /* background: linear-gradient(135deg, #fff, #e0f2f1); */
}

/* HERO SECTION */
.hero {
    position: relative;
    width: 100%;
    height: 90vh;
    display: flex;
    overflow: hidden;
}

.hero-background {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    overflow: hidden;
}

.hero-background img {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 1.5s ease-in-out;
}

.hero-background img.active {
    opacity: 1;
}

.hero-left {
    flex: 0 0 70%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4rem;
    background: rgba(240, 247, 246, 0.9);
    /* slightly transparent */
    clip-path: ellipse(78% 100% at 0% 50%);
    z-index: 2;
    position: relative;
}

.hero-content {
    max-width: 450px;
    margin: 0 auto;
}

.hero-kicker {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 1.5px;
}

.kicker-label {
    font-size: 1rem;
    font-weight: 700;
    color: #2e7d32;
}

.kicker-label::before {
    content: "";
    width: 40px;
    height: 3px;
    background: #b87c4c;
    display: block;
    margin-bottom: 0.2rem;
    border-radius: 2px;
}

.kicker-subline {
    font-size: 0.8rem;
    color: #444;
    font-weight: 500;
}

.hero-title {
    font-size: 3rem;
    line-height: 1.2;
    font-weight: 900;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.hero-subtitle {
    font-size: 1rem;
    color: #444;
    margin-bottom: 1.5rem;
    line-height: 1.5;
}

.hero-buttons {
    display: flex;
    gap: 1rem;
}

.btn-primary {
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    color: white;
    border-radius: 50px;
    padding: 0.8rem 2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(46, 125, 50, 0.4);
}

.btn-secondary {
    background: white;
    border: 2px solid #2e7d32;
    color: #2e7d32;
    border-radius: 50px;
    padding: 0.8rem 2rem;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-secondary:hover {
    background: #2e7d32;
    color: white;
    transform: translateY(-2px);
}

.learn-more-link {
    margin-top: 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #2e7d32;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: color 0.3s ease;
}

.learn-more-link:hover {
    color: #b87c4c;
    text-decoration: none;
}

.service-more {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: #555;
    line-height: 1.5;
    text-align: left;
}

/* WHY CHOOSE MNK */
.why-choose-section-modern {
    background-image: url(~@/assets/S1.jpg);
    background-size: cover;
    /* Makes the image cover the entire section */
    background-position: center;
    /* Centers the image */
    background-repeat: no-repeat;
    /* Prevents the image from repeating */
    padding: 6rem 1rem;
    color: #fff;
    /* Optional: ensures text is visible */
    position: relative;
    overflow: hidden;
}

/* Optional: Add a subtle overlay for better text readability */
.why-choose-section-modern::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.35);
    /* semi-transparent overlay */
    z-index: 1;
}

/* Make sure your content is above the overlay */
.why-choose-section-modern>* {
    position: relative;
    z-index: 2;
}

/* WHAT WE DO */
.what-we-do-section {
    padding: 4rem 1rem;
    background: linear-gradient(135deg, #fff, #e0f2f1);
}

.gradient-text {
    font-size: 2.5rem;
    font-weight: 900;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.summary-left .summary-content {
    max-width: 480px;
    margin: 0 auto;
}

.summary-left p {
    font-size: 1.2rem;
    color: #444;
    line-height: 1.5;
}

.summary-image {
    height: 600px;
    width: 100%;
    object-fit: cover;
    border-radius: 12px;
}

/*WHY CHOOSE MNK*/
.why-choose-section-modern {
    background-image: url(~@/assets/S6.jpg);
    padding: 6rem 1rem;
}

.why-title {
    font-size: 2.8rem;
    font-weight: 900;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

/* TIMELINE */
.timeline {
    position: relative;
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem 0;
}

/* CENTRAL LINE */
.timeline-line {
    position: absolute;
    top: 0;
    left: 50%;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #2e7d32, #b87c4c);
    transform: translateX(-50%);
    z-index: 0;
}

/* TIMELINE ITEM */
.timeline-item {
    position: relative;
    width: 100%;
    display: flex;
    margin-bottom: 4rem;
    align-items: flex-start;
}

/* NODE */
.timeline-node {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
}

.timeline-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.timeline-icon:hover {
    transform: scale(1.15);
    box-shadow: 0 12px 35px rgba(46, 125, 50, 0.4);
}

/* CARDS */
.timeline-card {
    position: relative;
    background: linear-gradient(135deg, #e6f1f0, #fff);
    border-radius: 16px;
    padding: 2rem;
    width: 45%;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.timeline-card.left {
    margin-right: auto;
    text-align: right;
}

.timeline-card.right {
    margin-left: auto;
    text-align: left;
}

.timeline-card::before {
    content: "";
    position: absolute;
    top: 50%;
    width: 20px;
    height: 4px;
    background: linear-gradient(to right, #2e7d32, #b87c4c);
}

.timeline-card.left::before {
    right: -20px;
}

.timeline-card.right::before {
    left: -20px;
}

/* CARD TEXT */
.timeline-card h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.5rem;
}

.timeline-card p {
    font-size: 0.95rem;
    color: #555;
    line-height: 1.5;
}

/* RESPONSIVE */
@media (max-width: 768px) {

    .timeline-card,
    .timeline-card.left,
    .timeline-card.right {
        width: 90%;
        text-align: center;
        margin: 2rem auto !important;
    }

    .timeline-card::before {
        display: none;
    }
}

/* WHAT WE DO NEW SECTION */
.what-we-do-pro {
    padding: 8rem 1rem;
    background: #fff;
}

/* ===== WHAT WE DO MODERN ===== */
.what-we-do-modern {
    padding: 4rem 1rem;
    background: linear-gradient(135deg, #fff, #e0f2f1);
}

.service-card {
    background: linear-gradient(135deg, #f8f8f8 0%, #edf7f6 50%, #f2fffd 100%);
    border-radius: 20px;
    padding: 2rem 1.5rem;
    text-align: center;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
}

.service-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(46, 125, 50, 0.12);
    /* subtle brand-colored shadow */
}

.icon-wrapper {
    margin-bottom: 1rem;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e0f2f1, #c8e6c9);
    display: flex;
    align-items: center;
    justify-content: center;
}

.service-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.5rem;
}

.service-description {
    font-size: 0.95rem;
    color: #555;
    line-height: 1.5;
}

/* Responsive */

@media (max-width: 768px) {
    .service-card {
        padding: 1.5rem 1rem;
    }
}

@media (max-width: 480px) {
    .service-card {
        padding: 1rem;
    }
}

.central-image-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 3rem;
}

.central-image {
    width: 230px;
    height: 230px;
    border-radius: 50%;
    object-fit: cover;
    border: 6px solid #e0f2f1;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.central-image:hover {
    transform: scale(1.05);
    box-shadow: 0 16px 50px rgba(46, 125, 50, 0.25);
}

/* ===== TESTIMONIALS ===== */
.testimonials-section {
    padding: 6rem 1rem;

    position: relative;
}

/* Slider wrapper */
.testimonial-slider {
    overflow: hidden;
    position: relative;
    width: 100%;
    margin: 0 auto;
    /* centers the whole slider */
}

/* Track that moves left/right */
.testimonial-track {
    display: flex;
    transition: transform 0.8s ease-in-out;
    justify-content: center;
    /* centers all items if fewer than full row */
}

/* Each testimonial slide */
.testimonial-slide {
    flex: 0 0 calc(100% / 3);
    /* 3 items per view */
    padding: 1rem;
    box-sizing: border-box;
    justify-content: center;
    /* centers the card inside */
}

.testimonial-card {
    background: linear-gradient(135deg, #f9fdfc, #f5eee8);
    border-radius: 20px;
    padding: 2rem 1.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: space-between;
}

.testimonial-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 45px rgba(46, 125, 50, 0.15);
}

.testimonial-text {
    font-size: 1rem;
    line-height: 1.6;
    color: #444;
    margin-bottom: 1.5rem;
    font-style: italic;
    position: relative;
}

.testimonial-text::before {
    content: "“";
    font-size: 2.5rem;
    color: #2e7d32;
    position: absolute;
    top: -15px;
    left: -10px;
    opacity: 0.15;
}

.testimonial-name {
    font-weight: 700;
    font-size: 1rem;
    color: #2e7d32;
    text-align: right;
}

/* .image-wrapper img {
   
    opacity: 0;
    transition: opacity 1.5s ease-in-out;
}

.image-wrapper img.active {
    opacity: 1;
} */

/* ===== EXPORT LUX ===== */
.export-lux-section {
    padding: 6rem 1rem;
    /* background: radial-gradient(circle at top, #f7fbfa, #edf4f2); */
}

/* HEADER */
.lux-header {
    text-align: center;
    max-width: 780px;
    margin: 0 auto 5rem;
}

/* Column with vertical dotted separator */
.lux-stat-col {
    border-right: 1px dotted #b0b0b0;
    display: flex;
    justify-content: center;
    align-items: stretch;
    padding: 0.5rem;
}

/* Remove right border for last column */
.lux-stat-col:last-child {
    border-right: none;
}

/* Stat card styling */
.lux-stat-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 1rem 0;
    /* transition: transform 0.3s ease; */
}

/* Centered icon styling */
.lux-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    /* background: linear-gradient(135deg, #2e7d32, #b87c4c); */
    display: flex;
    align-items: center;
    justify-content: center;
    /* box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15); */
    margin-bottom: 0.8rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

/* Hover effect for icon */
.lux-stat-card:hover .lux-icon {
    transform: scale(1.1);
    /* box-shadow: 0 12px 35px rgba(46, 125, 50, 0.35); */
}

/* Stat value */
.lux-stat-value {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.3rem;
}

/* Label */
.lux-stat-label {
    font-size: 0.95rem;
    color: #555;
    text-align: center;
}

/* Responsive: remove vertical borders on smaller screens */
@media (max-width: 768px) {
    .lux-stat-col {
        border-right: none;
        border-bottom: 1px dotted #b0b0b0;
        margin-bottom: 1rem;
    }

    .lux-stat-col:last-child {
        border-bottom: none;
    }
}

.lux-eyebrow {
    font-size: .75rem;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: #b87c4c;
    font-weight: 700;
}

.lux-title {
    font-size: 3.2rem;
    font-weight: 900;
    margin: .6rem 0;
    background: linear-gradient(135deg, #2e7d32, #b87c4c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.lux-divider {
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #2e7d32, #b87c4c);
    margin: 1.5rem auto;
    border-radius: 4px;
}

.lux-subtitle {
    font-size: 1.1rem;
    color: #555;
    line-height: 1.7;
    font-style: italic;
    /* padding: 30px 0 70px 0; */
    /* font-weight: 500; */
    text-align: center;
    margin-bottom: 30px;
}

/* STATS */

.floating {
    animation: float 6s ease-in-out infinite;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-12px);
    }
}

/* CROPS */
.lux-crop-card {
    position: relative;
    height: 250px;
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 22px 55px rgba(0, 0, 0, .12);
}

.lux-crop-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.lux-crop-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, .75), transparent);
    padding: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    color: #fff;
}

/* COUNTRIES */
.lux-countries-wrapper {
    overflow: hidden;
    position: relative;

}

.lux-countries-track {
    display: flex;
    gap: 30px;
    animation: scrollCountries 35s linear infinite;
}

@keyframes scrollCountries {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-50%);
    }
}

.lux-country-card {
    min-width: 250px;
    background: rgba(255, 255, 255, .75);
    backdrop-filter: blur(12px);
    border-radius: 22px;
    padding: 1.4rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
    transition: transform .4s ease;
}

.lux-country-card:hover {
    transform: translateY(-10px) scale(1.05);
}

.lux-country-card img {
    width: 100px;
    margin-bottom: .6rem;
}

.lux-country-card span {
    font-weight: 600;
    font-size: .9rem;
}

.lux-section-title {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-align: center;
    margin-top: 48px;
    margin-bottom: 36px;
    position: relative;

    background: linear-gradient(90deg, #2e7d32, #7bc8a4);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

/* decorative underline */
.lux-section-title::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -14px;
    transform: translateX(-50%);
    width: 90px;
    height: 4px;
    background: linear-gradient(to right, #2e7d32, #7bc8a4);
    border-radius: 12px;
}

/* subtle fade-up entrance */
.lux-section-title {
    animation: titleReveal 0.9s ease forwards;
}

@keyframes titleReveal {
    from {
        opacity: 0;
        transform: translateY(16px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
